import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { planningApi as P } from "@/lib/planning";
import { STAGE_TITLE } from "@/lib/production";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Plus, Trash2 } from "lucide-react";

const ROUTE = ["printing", "fusing", "cutting", "stitching", "packing"];

export default function JobForm() {
  const { id } = useParams();
  const editing = !!id;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sos, setSos] = useState([]);
  const [machines, setMachines] = useState([]);
  const [f, setF] = useState({
    so_id: "", line_item_id: "", target_meters: "", route: [...ROUTE], target_date: "",
    sequence: "", estimated_hours: "", supervisor_name: "", machine_assignments: {},
    reserved_paper_rolls: [{ roll_id: "", meters: "" }],
    reserved_fabric_rolls: [{ roll_id: "", meters: "" }], allowed_wastage_pct: 5, allowed_shrinkage_pct: 6,
  });

  useEffect(() => {
    (async () => {
      const [soList, mList] = await Promise.all([P.salesOrders(), P.machines()]);
      setSos(soList); setMachines(mList);
      if (editing) {
        const j = await P.job(id);
        if (!["Planning", "Ready for Production"].includes(j.status)) { toast.error("Only jobs in Planning can be edited."); navigate(`/production-planning/jobs/${id}`); return; }
        setF({
          so_id: j.so_id, line_item_id: j.line_item_id, target_meters: j.target_meters, route: j.route || [...ROUTE],
          target_date: j.target_date || "", sequence: j.sequence || "", estimated_hours: j.estimated_hours || "",
          supervisor_name: j.supervisor_name || "", machine_assignments: j.machine_assignments || {},
          reserved_paper_rolls: (j.reserved_paper_rolls?.length ? j.reserved_paper_rolls : [{ roll_id: "", meters: "" }]),
          reserved_fabric_rolls: (j.reserved_fabric_rolls?.length ? j.reserved_fabric_rolls : [{ roll_id: "", meters: "" }]),
          allowed_wastage_pct: j.allowed_wastage_pct ?? 5, allowed_shrinkage_pct: j.allowed_shrinkage_pct ?? 6,
        });
        // ensure the SO of an editing job is present in dropdown
        if (!soList.find((s) => s.id === j.so_id)) setSos((p) => [...p, { id: j.so_id, so_no: j.so_no, customer: j.customer, items: [j.item ? { ...j.item, id: j.line_item_id } : {}] }]);
      }
      setLoading(false);
    })();
  }, [id, editing, navigate]);

  const upd = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const selectedSo = sos.find((s) => s.id === f.so_id);
  const items = selectedSo?.items || [];
  const selectedItem = items.find((i) => i.id === f.line_item_id);

  const onSelectItem = (itemId) => {
    const it = items.find((i) => i.id === itemId);
    setF((p) => ({ ...p, line_item_id: itemId, target_meters: p.target_meters || (it?.quantity ?? "") }));
  };

  const toggleStage = (st) => setF((p) => ({ ...p, route: p.route.includes(st) ? p.route.filter((x) => x !== st) : [...ROUTE].filter((x) => p.route.includes(x) || x === st) }));
  const setMachine = (st, mid) => setF((p) => ({ ...p, machine_assignments: { ...p.machine_assignments, [st]: mid } }));
  const updRoll = (i, k, v) => setF((p) => ({ ...p, reserved_fabric_rolls: p.reserved_fabric_rolls.map((r, idx) => idx === i ? { ...r, [k]: v } : r) }));
  const addRoll = () => setF((p) => ({ ...p, reserved_fabric_rolls: [...p.reserved_fabric_rolls, { roll_id: "", meters: "" }] }));
  const removeRoll = (i) => setF((p) => ({ ...p, reserved_fabric_rolls: p.reserved_fabric_rolls.filter((_, idx) => idx !== i) }));
  const updPaper = (i, k, v) => setF((p) => ({ ...p, reserved_paper_rolls: p.reserved_paper_rolls.map((r, idx) => idx === i ? { ...r, [k]: v } : r) }));
  const addPaper = () => setF((p) => ({ ...p, reserved_paper_rolls: [...p.reserved_paper_rolls, { roll_id: "", meters: "" }] }));
  const removePaper = (i) => setF((p) => ({ ...p, reserved_paper_rolls: p.reserved_paper_rolls.filter((_, idx) => idx !== i) }));

  const reservedTotal = useMemo(() => f.reserved_fabric_rolls.reduce((s, r) => s + (Number(r.meters) || 0), 0), [f.reserved_fabric_rolls]);
  const paperTotal = useMemo(() => f.reserved_paper_rolls.reduce((s, r) => s + (Number(r.meters) || 0), 0), [f.reserved_paper_rolls]);

  const save = async () => {
    if (!f.so_id || !f.line_item_id) { toast.error("Select a Sales Order and line item"); return; }
    setSaving(true);
    try {
      const rolls = f.reserved_fabric_rolls.filter((r) => r.roll_id && Number(r.meters) > 0).map((r) => ({ roll_id: r.roll_id, meters: Number(r.meters) }));
      const paperRolls = f.reserved_paper_rolls.filter((r) => r.roll_id && Number(r.meters) > 0).map((r) => ({ roll_id: r.roll_id, meters: Number(r.meters) }));
      const payload = {
        so_id: f.so_id, line_item_id: f.line_item_id, target_meters: Number(f.target_meters) || 0,
        route: f.route, target_date: f.target_date || null, sequence: Number(f.sequence) || 0,
        estimated_hours: Number(f.estimated_hours) || 0, supervisor_name: f.supervisor_name,
        machine_assignments: f.machine_assignments, reserved_paper_rolls: paperRolls, reserved_fabric_rolls: rolls,
        allowed_wastage_pct: Number(f.allowed_wastage_pct), allowed_shrinkage_pct: Number(f.allowed_shrinkage_pct),
      };
      const res = editing ? await P.updateJob(id, payload) : await P.createJob(payload);
      toast.success(editing ? "Job updated" : "Production job created");
      navigate(`/production-planning/jobs/${res.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>;

  return (
    <div className="space-y-6" data-testid="job-form-page">
      <button onClick={() => navigate("/production-planning")} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"><ArrowLeft className="h-4 w-4" />Back</button>
      <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">{editing ? "Edit Production Job" : "New Production Job"}</h2>

      <Card className="p-6">
        <h3 className="mb-4 font-heading font-semibold text-slate-900">Source (Confirmed Sales Order)</h3>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5"><Label>Sales Order *</Label>
            <Select value={f.so_id} onValueChange={(v) => setF((p) => ({ ...p, so_id: v, line_item_id: "" }))} disabled={editing}>
              <SelectTrigger data-testid="job-so-select"><SelectValue placeholder="Select Confirmed SO" /></SelectTrigger>
              <SelectContent>{sos.map((s) => <SelectItem key={s.id} value={s.id}>{s.so_no} · {s.customer?.company_name}</SelectItem>)}</SelectContent>
            </Select>
            {sos.length === 0 && <p className="text-xs text-amber-600">No Confirmed Sales Orders available to plan.</p>}
          </div>
          <div className="space-y-1.5"><Label>Line item *</Label>
            <Select value={f.line_item_id} onValueChange={onSelectItem} disabled={!f.so_id || editing}>
              <SelectTrigger data-testid="job-item-select"><SelectValue placeholder="Select line item" /></SelectTrigger>
              <SelectContent>{items.map((it) => <SelectItem key={it.id} value={it.id}>{it.fabric_type} · {it.quantity} {it.unit}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        {selectedItem && <p className="mt-3 text-sm text-slate-500">Artwork: {selectedSo?.artwork ? `${selectedSo.artwork.artwork_code} (V${selectedSo.artwork.version}, locked)` : "None on this SO — printing will be blocked"}</p>}
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="p-6">
          <h3 className="mb-4 font-heading font-semibold text-slate-900">Plan</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Target meters *</Label><Input type="number" value={f.target_meters} onChange={(e) => upd("target_meters", e.target.value)} data-testid="job-target-input" /></div>
            <div className="space-y-1.5"><Label>Target date *</Label><Input type="date" value={f.target_date} onChange={(e) => upd("target_date", e.target.value)} data-testid="job-date-input" /></div>
            <div className="space-y-1.5"><Label>Sequence</Label><Input type="number" value={f.sequence} onChange={(e) => upd("sequence", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Est. hours</Label><Input type="number" value={f.estimated_hours} onChange={(e) => upd("estimated_hours", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Supervisor</Label><Input value={f.supervisor_name} onChange={(e) => upd("supervisor_name", e.target.value)} data-testid="job-supervisor-input" /></div>
            <div className="space-y-1.5"><Label>Allowed wastage %</Label><Input type="number" value={f.allowed_wastage_pct} onChange={(e) => upd("allowed_wastage_pct", e.target.value)} /></div>
          </div>
          <div className="mt-4">
            <Label className="text-xs uppercase tracking-wider text-slate-500">Production route & machines</Label>
            <div className="mt-2 space-y-2">
              {ROUTE.map((st) => (
                <div key={st} className="flex items-center gap-3">
                  <label className="flex w-32 items-center gap-2">
                    <Checkbox checked={f.route.includes(st)} onCheckedChange={() => toggleStage(st)} data-testid={`route-${st}`} />{STAGE_TITLE[st] || st}
                  </label>
                  <Select value={f.machine_assignments[st] || ""} onValueChange={(v) => setMachine(st, v)} disabled={!f.route.includes(st)}>
                    <SelectTrigger className="flex-1" data-testid={`machine-select-${st}`}><SelectValue placeholder="Assign machine" /></SelectTrigger>
                    <SelectContent>{machines.filter((m) => m.stage === st).map((m) => <SelectItem key={m.machine_id} value={m.machine_id}>{m.machine_id} · {m.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-heading font-semibold text-slate-900">Reserved sublimation paper rolls</h3>
            <Button size="sm" variant="outline" onClick={addPaper} data-testid="add-paper-btn"><Plus className="mr-1 h-3.5 w-3.5" />Add roll</Button>
          </div>
          <p className="mb-3 text-xs text-slate-500">Blank paper consumed at <span className="font-medium">Printing</span> to produce the printed paper roll. References only — no inventory deduction.</p>
          <div className="space-y-2">
            {f.reserved_paper_rolls.map((r, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`paper-${i}`}>
                <Input placeholder="Paper roll ID" value={r.roll_id} onChange={(e) => updPaper(i, "roll_id", e.target.value)} data-testid={`paper-id-${i}`} />
                <Input type="number" placeholder="Meters" value={r.meters} onChange={(e) => updPaper(i, "meters", e.target.value)} className="w-32" data-testid={`paper-meters-${i}`} />
                {f.reserved_paper_rolls.length > 1 && <button onClick={() => removePaper(i)} className="text-rose-600"><Trash2 className="h-4 w-4" /></button>}
              </div>
            ))}
          </div>
          <p className="mt-3 text-sm text-slate-600">Paper reserved: <span className="font-medium">{paperTotal} m</span></p>
        </Card>

        <Card className="p-6">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-heading font-semibold text-slate-900">Reserved grey fabric rolls</h3>
            <Button size="sm" variant="outline" onClick={addRoll} data-testid="add-roll-btn"><Plus className="mr-1 h-3.5 w-3.5" />Add roll</Button>
          </div>
          <p className="mb-3 text-xs text-slate-500">Grey fabric consumed at <span className="font-medium">Fusing</span> together with the printed paper roll to create the finished fabric roll. References only — no inventory deduction.</p>
          <div className="space-y-2">
            {f.reserved_fabric_rolls.map((r, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`roll-${i}`}>
                <Input placeholder="Grey fabric roll ID" value={r.roll_id} onChange={(e) => updRoll(i, "roll_id", e.target.value)} data-testid={`roll-id-${i}`} />
                <Input type="number" placeholder="Meters" value={r.meters} onChange={(e) => updRoll(i, "meters", e.target.value)} className="w-32" data-testid={`roll-meters-${i}`} />
                {f.reserved_fabric_rolls.length > 1 && <button onClick={() => removeRoll(i)} className="text-rose-600"><Trash2 className="h-4 w-4" /></button>}
              </div>
            ))}
          </div>
          <p className="mt-3 text-sm text-slate-600">Fabric reserved: <span className="font-medium">{reservedTotal} m</span> {Number(f.target_meters) > 0 && reservedTotal < Number(f.target_meters) && <span className="text-amber-600">(below target)</span>}</p>
        </Card>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => navigate("/production-planning")}>Cancel</Button>
        <Button onClick={save} disabled={saving} data-testid="save-job-btn" className="bg-indigo-600 text-white hover:bg-indigo-700">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{editing ? "Save changes" : "Create job"}</Button>
      </div>
    </div>
  );
}
